import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { Subscription, combineLatest } from 'rxjs';
import { tap } from 'rxjs/operators';

import { FCreateConnectionEvent, FFlowModule, FSelectionChangeEvent } from '@foblex/flow';


// PrimeNG Modules
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService, SelectItem } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';
import { ToolbarModule } from 'primeng/toolbar';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextarea } from 'primeng/inputtextarea';

import { WorkflowConfigService } from './workflow-config.service';
import { WorkflowStatusMaster } from './workflow-status.model';
import { WorkflowTransitionConfig } from './workflow-transition.model';
import { ResourceService } from '../master-data/resource/resource.service';
import { Resource } from '../master-data/resource/resource.model';
import { ActivityType } from '../master-data/activity-type/activity-type.model';
import { ActivityTypeService } from '../master-data/activity-type/activity-type.service';
import { WorkflowTransitionCondition } from './workflow-transition-condition.model';

interface WorkflowGraphNode {
  id: string;
  status: WorkflowStatusMaster;
  position: { x: number; y: number };
}

@Component({
  selector: 'app-status-workflow-manager',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    FFlowModule,
    ButtonModule,
    DropdownModule,
    DialogModule,
    InputTextModule,
    ConfirmDialogModule,
    ToastModule,
    ToolbarModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextarea,
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './workflow-config.component.html',
  styleUrls: ['./workflow-config.component.css']
})
export class StatusWorkflowManagerComponent implements OnInit, OnDestroy {

  workflowNodes: WorkflowGraphNode[] = [];
  visibleTransitions: WorkflowTransitionConfig[] = [];
  statuses: WorkflowStatusMaster[] = []; // Local copy for dropdowns etc.
  transitions: WorkflowTransitionConfig[] = [];
  activityTypes: ActivityType[] = [];
  selectedWorkflowActivityType: ActivityType | null = null;

  displayStatusDialog: boolean = false;
  statusForm!: FormGroup;
  editingStatus: WorkflowStatusMaster | null = null;

  displayTransitionDialog: boolean = false;
  transitionForm!: FormGroup;
  editingTransition: WorkflowTransitionConfig | null = null;

  displayConditionDialog: boolean = false;
  conditionForm!: FormGroup;
  selectedTransition: WorkflowTransitionConfig | null = null;
  selectedStatus: WorkflowStatusMaster | null = null;
  selectedNodeId: string | null = null;
  selectedEdgeId: string | null = null;
  conditions: WorkflowTransitionCondition[] = [];
  editingCondition: WorkflowTransitionCondition | null = null;

  selectedResource: Resource | null = null;
  resources: Resource[] = [];

  statusOptions: SelectItem[] = [];


  isBrowser: boolean;
  private dataSub!: Subscription;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private fb: FormBuilder,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private cdRef: ChangeDetectorRef,
    private workflowConfigService: WorkflowConfigService,
    private resourceService: ResourceService,
    private activityTypeService: ActivityTypeService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.loadResources();
    this.loadActivityTypes();
    this.initStatusForm();
    this.initTransitionForm();
    this.initConditionForm();
  }

  loadActivityTypes(): void {
    const params = new HttpParams().set('page', '0').set('size', '1000');
    this.activityTypeService.getAllActivityTypes(params).subscribe(page => {
      this.activityTypes = page?.content ?? [];
    });
  }

  loadResources(): void {
    const params = new HttpParams().set('page', '0').set('size', '1000');
    this.resourceService.getAllResources(params).subscribe(page => {
      if (page && page.content) {
        this.resources = page.content;
      }
    });
  }

  loadWorkflowData() {
    if (!this.selectedResource) {
      this.workflowNodes = [];
      this.visibleTransitions = [];
      this.statuses = [];
      this.transitions = [];
      this.clearSelection();
      this.cdRef.detectChanges(); // Trigger change detection after clearing data
      return;
    }

    this.dataSub = combineLatest([
      this.workflowConfigService.getAllStatuses(new HttpParams().set('resourceType.id', this.selectedResource.id!)),
      this.workflowConfigService.getAllTransitions(new HttpParams().set('resourceType.id', this.selectedResource.id!))
    ]).pipe(
      tap(([statusesPage, transitionsPage]) => {
        this.statuses = statusesPage.content; // Store statuses
        this.transitions = transitionsPage.content; // Store Transitions
        this.statusOptions = this.statuses.map(s => ({ label: s.statusName, value: s }));
      }),
    ).subscribe(() => {
      this.buildGraph();
      this.reconcileSelection();
      this.cdRef.detectChanges(); // Trigger change detection if needed
    });
  }

  private buildGraph(): void {
    const existingPositions = new Map(this.workflowNodes.map(node => [node.id, node.position]));
    this.workflowNodes = this.statuses.map((status, index) => ({
      id: String(status.id),
      status,
      position: existingPositions.get(String(status.id)) ?? {
        x: 80 + (index % 3) * 360,
        y: 80 + Math.floor(index / 3) * 190
      }
    }));
    this.visibleTransitions = this.transitions.filter(transition =>
      (transition.activityType?.id ?? null) === (this.selectedWorkflowActivityType?.id ?? null)
    );
  }

  initStatusForm(): void {
    this.statusForm = this.fb.group({
      statusName: ['', Validators.required],
      description: [''],
      resourceType: [this.selectedResource, Validators.required],
    });
  }

  initTransitionForm(): void {
     this.transitionForm = this.fb.group({
      currentStatus: [null, Validators.required],
      nextStatus: [null, Validators.required],
      actionName: [''],
      resourceType: [this.selectedResource, Validators.required],
      activityType: [this.selectedWorkflowActivityType]
    });
  }

  initConditionForm(): void {
    this.conditionForm = this.fb.group({
      tableName: ['', Validators.required],
      columnName: ['', Validators.required],
      operator: ['', Validators.required],
      value: ['', Validators.required],
      activityType: [this.selectedWorkflowActivityType]
    });
  }

  onResourceChange(event: any): void {
    this.selectedResource = event.value;
    this.clearSelection();
    this.loadWorkflowData();
    this.initStatusForm();
    this.initTransitionForm();
  }

  onWorkflowActivityTypeChange(): void {
    this.clearSelection();
    this.loadWorkflowData();
  }

  // --- Status CRUD ---



  openNewStatusDialog(): void {
    this.editingStatus = null;
    this.statusForm.reset({ resourceType: this.selectedResource });
    this.displayStatusDialog = true;
  }

  openEditStatusDialog(statusToEdit: WorkflowStatusMaster): void {
    this.workflowConfigService.getStatusById(Number(statusToEdit.id)).subscribe(status => {
      if (status) {
        this.editingStatus = status;
        this.statusForm.patchValue(status);
         this.displayStatusDialog = true;
      }
    });
  }

  saveStatus(): void {
    if (this.statusForm.invalid) {
      this.messageService.add({severity:'warn', summary: 'Validation Error', detail: 'Please fill in all required fields.'});
      return;
     }
    const formData = this.statusForm.value;
    if (this.editingStatus) {
      this.workflowConfigService.updateStatus(this.editingStatus.id!, formData)
        .subscribe(() => {
          this.messageService.add({severity:'success', summary: 'Success', detail: 'Status updated successfully.'});
          this.loadWorkflowData();
         });
    } else {
      this.workflowConfigService.createStatus(formData)
        .subscribe(() => {
          this.messageService.add({severity:'success', summary: 'Success', detail: 'Status added successfully.'});
          this.loadWorkflowData();
        });
    }
    this.displayStatusDialog = false;
  }

  deleteStatus(status: WorkflowStatusMaster): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete status "${status.statusName}" and its associated transitions?`,
      header:  'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.workflowConfigService.deleteStatus(Number(status.id)).subscribe(() => {
          this.messageService.add({severity:'success', summary:  'Success', detail: 'Status deleted successfully.'});
          this.clearSelection();
          this.loadWorkflowData();
        });
      }
    });
  }

  // --- Transition CRUD ---
  openNewTransitionDialog(): void {
    this.editingTransition = null;
    this.transitionForm.reset({ resourceType: this.selectedResource, activityType: this.selectedWorkflowActivityType });
    this.displayTransitionDialog = true;
  }

  openNewTransitionFromSelectedStatus(): void {
    this.openNewTransitionDialog();
    if (this.selectedStatus) {
      this.transitionForm.patchValue({ currentStatus: this.selectedStatus });
    }
  }

  openEditTransitionDialog(transition: WorkflowTransitionConfig): void {
    if (transition) {
      this.editingTransition = transition;
      this.selectedTransition = transition;
      this.transitionForm.patchValue({
        currentStatus: transition.currentStatus,
        nextStatus: transition.nextStatus,
        actionName: transition.actionName,
        resourceType: transition.resourceType,
        activityType: transition.activityType ?? null,
      });
      this.displayTransitionDialog = true;
    }
   }

  saveTransition(): void {
    if (this.transitionForm.invalid) {
      this.messageService.add({severity:'warn', summary: 'Validation Error', detail: 'Please select source and target statuses.'});
      return;
    }
    const formData = this.transitionForm.value;
    if (this.editingTransition) {
      this.workflowConfigService.updateTransition(this.editingTransition.id!, formData)
        .subscribe(() => {
          this.messageService.add({severity:'success', summary: 'Success', detail: 'Transition updated successfully.'});
          this.loadWorkflowData();
        });
    } else {
      this.workflowConfigService.createTransition(formData)
        .subscribe(() => {
          this.messageService.add({severity:'success', summary: 'Success', detail: 'Transition added successfully.'});
          this.loadWorkflowData();
        });
    }
    this.displayTransitionDialog = false;
  }

  deleteTransition(transition: WorkflowTransitionConfig): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete this transition?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        if (transition.id !== undefined) {
          this.workflowConfigService.deleteTransition(Number(transition.id)).subscribe(() => {
            this.messageService.add({severity:'success', summary: 'Success', detail: 'Transition deleted successfully.'});
            this.clearSelection();
            this.loadWorkflowData();
          });
        }
      }
    });
  }

  openConditionsDialog(): void {
    if (!this.selectedTransition?.id) {
      return;
    }
    this.editingCondition = null;
    this.conditionForm.reset({ activityType: this.selectedWorkflowActivityType });
    this.loadConditions();
    this.displayConditionDialog = true;
  }

  loadConditions(): void {
    if (!this.selectedTransition?.id) {
      this.conditions = [];
      return;
    }
    this.workflowConfigService.getConditionsByTransitionId(
      this.selectedTransition.id,
      new HttpParams().set('page', '0').set('size', '1000')
    ).subscribe(page => {
      this.conditions = (page?.content ?? []).filter(condition =>
        (condition.activityType?.id ?? null) === (this.selectedWorkflowActivityType?.id ?? null)
      );
    });
  }

  editCondition(condition: WorkflowTransitionCondition): void {
    this.editingCondition = condition;
    this.conditionForm.patchValue(condition);
  }

  saveCondition(): void {
    if (!this.selectedTransition?.id || this.conditionForm.invalid) {
      this.conditionForm.markAllAsTouched();
      return;
    }
    const formData: WorkflowTransitionCondition = {
      ...this.conditionForm.value,
      id: this.editingCondition?.id,
      transitionId: this.selectedTransition.id
    };
    const request = this.editingCondition?.id
      ? this.workflowConfigService.updateCondition(this.selectedTransition.id, this.editingCondition.id, formData)
      : this.workflowConfigService.createCondition(this.selectedTransition.id, formData);
    request.subscribe(() => {
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Condition saved successfully.' });
      this.editingCondition = null;
      this.conditionForm.reset({ activityType: this.selectedWorkflowActivityType });
      this.loadConditions();
    });
  }

  deleteCondition(condition: WorkflowTransitionCondition): void {
    if (!this.selectedTransition?.id || !condition.id) {
      return;
    }
    this.workflowConfigService.deleteCondition(this.selectedTransition.id, condition.id).subscribe(() => {
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Condition deleted successfully.' });
      this.loadConditions();
    });
  }

  selectNode(status: WorkflowStatusMaster): void {
    this.selectedNodeId = String(status.id);
    this.selectedEdgeId = null;
    this.selectedStatus = status;
    this.selectedTransition = null;
    this.cdRef.detectChanges();
  }

  selectEdge(transition: WorkflowTransitionConfig): void {
    this.selectedEdgeId = String(transition.id);
    this.selectedNodeId = null;
    this.selectedStatus = null;
    this.selectedTransition = transition;
    this.cdRef.detectChanges();
  }

  clearSelection(): void {
    this.selectedNodeId = null;
    this.selectedEdgeId = null;
    this.selectedStatus = null;
    this.selectedTransition = null;
  }

  editSelectedStatus(): void {
    if (this.selectedStatus) {
      this.openEditStatusDialog(this.selectedStatus);
    }
  }

  deleteSelectedStatus(): void {
    if (this.selectedStatus) {
      this.deleteStatus(this.selectedStatus);
    }
  }

  editSelectedTransition(): void {
    if (this.selectedTransition) {
      this.openEditTransitionDialog(this.selectedTransition);
    }
  }

  deleteSelectedTransition(): void {
    if (this.selectedTransition) {
      this.deleteTransition(this.selectedTransition);
    }
  }

  onFlowSelectionChange(event: FSelectionChangeEvent): void {
    const selectedNodeId = event.nodeIds[0];
    const selectedTransitionId = event.connectionIds[0];
    if (selectedNodeId) {
      const node = this.workflowNodes.find(item => item.id === selectedNodeId);
      if (node) this.selectNode(node.status);
      return;
    }
    if (selectedTransitionId) {
      const transition = this.visibleTransitions.find(item => String(item.id) === selectedTransitionId);
      if (transition) this.selectEdge(transition);
      return;
    }
    this.clearSelection();
    this.cdRef.detectChanges();
  }

  onCreateConnection(event: FCreateConnectionEvent): void {
    if (!event.targetId) return;
    const sourceStatusId = event.sourceId.replace(/-out$/, '');
    const targetStatusId = event.targetId.replace(/-in$/, '');
    const currentStatus = this.statuses.find(status => String(status.id) === sourceStatusId);
    const nextStatus = this.statuses.find(status => String(status.id) === targetStatusId);
    if (!currentStatus || !nextStatus || currentStatus.id === nextStatus.id) return;

    this.openNewTransitionDialog();
    this.transitionForm.patchValue({ currentStatus, nextStatus });
    this.messageService.add({ severity: 'info', summary: 'New transition', detail: 'Complete the transition details to save this connection.' });
  }

  onNodePositionChange(nodeId: string, position: { x: number; y: number }): void {
    this.workflowNodes = this.workflowNodes.map(node => node.id === nodeId ? { ...node, position } : node);
  }

  private reconcileSelection(): void {
    if (this.selectedNodeId && !this.workflowNodes.some(node => String(node.id) === this.selectedNodeId)) {
      this.clearSelection();
    }
    if (this.selectedEdgeId && !this.visibleTransitions.some(transition => String(transition.id) === this.selectedEdgeId)) {
      this.clearSelection();
    }
  }

  getStatusColor(status: WorkflowStatusMaster): string {
    const name = status.statusName.toLowerCase();
    if (status.isFinalStatus || /cancel|reject|fail|not settled/.test(name)) return '#c2415b';
    if (/approv|settled|complete|done|end/.test(name)) return '#16a36a';
    if (/hold|attention|review|draft/.test(name)) return '#4f78c9';
    return '#0f8fae';
  }

  ngOnDestroy(): void {
    if (this.dataSub) {
      this.dataSub.unsubscribe();
    }
  }

}
